/**
 * VerifyEmail.js
 * Handles email verification from the verification link
 * Same pattern as VerifyMagicLink but redirects to login instead of logging in
 */

import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { authService } from '../../services/api';
import './VerifyEmail.css';

const VerifyEmail = () => {
  const { token } = useParams();
  const navigate = useNavigate();

  const [status, setStatus] = useState({
    type: 'loading', // 'loading' | 'success' | 'error'
    message: 'Verifying your email...'
  });

  // Ref guard to prevent duplicate verification calls in StrictMode
  const hasVerifiedRef = useRef(false);

  useEffect(() => {
    const verifyToken = async () => {
      // Prevent duplicate API calls (React StrictMode runs effects twice in dev)
      if (hasVerifiedRef.current) {
        console.log('Email verification already initiated, skipping duplicate call');
        return;
      }

      hasVerifiedRef.current = true;

      if (!token) {
        setStatus({
          type: 'error',
          message: 'Invalid verification link'
        });
        return;
      }

      try {
        console.log('Verifying email token...');

        const response = await authService.verifyEmail(token);

        if (response.success) {
          console.log('Email verified successfully');

          setStatus({
            type: 'success',
            message: response.alreadyVerified
              ? 'Your email is already verified!'
              : 'Email verified successfully!'
          });

          // Redirect to login after 2 seconds
          setTimeout(() => {
            navigate('/login');
          }, 2000);
        } else {
          setStatus({
            type: 'error',
            message: response.message || 'Email verification failed'
          });
        }

      } catch (error) {
        console.error('Email verification error:', error);

        setStatus({
          type: 'error',
          message: error.response?.data?.error ||
            'This verification link is invalid or has expired.'
        });
      }
    };

    verifyToken();
  }, [token, navigate]);

  return (
    <div className="verify-email-container">
      <div className="verify-email-card">
        {status.type === 'loading' && (
          <div className="verify-status loading">
            <div className="spinner"></div>
            <h2>Verifying Your Email</h2>
            <p>Please wait...</p>
          </div>
        )}

        {status.type === 'success' && (
          <div className="verify-status success">
            <div className="success-icon">&#10003;</div>
            <h2>{status.message}</h2>
            <p>Redirecting you to login...</p>
          </div>
        )}

        {status.type === 'error' && (
          <div className="verify-status error">
            <div className="error-icon">&#10005;</div>
            <h2>Verification Failed</h2>
            <p className="error-message">{status.message}</p>

            <div className="error-actions">
              <Link to="/join-waitlist" className="button primary">
                Join the Waitlist
              </Link>
              <Link to="/login" className="button secondary">
                Back to Login
              </Link>
            </div>

            <div className="error-help">
              <p className="help-text">
                <strong>Common reasons:</strong>
              </p>
              <ul>
                <li>The link has expired (links expire after 24 hours)</li>
                <li>The link was copied incorrectly</li>
                <li>A newer verification email was sent</li>
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VerifyEmail;
